// Shared template for the seven named service landing pages.
// Aggregates existing services from /data/services.js under a durable named URL.
// Injects Service + FAQPage + WebPage + Breadcrumb JSON-LD per landing.
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SITE } from "@/data/config";
import { SERVICES } from "@/data/services";
import { SERVICE_LANDINGS } from "@/data/serviceLandings";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import ServiceCard from "@/components/ServiceCard";
import CTABlock from "@/components/CTABlock";
import HubHero, { HUB_SLUGS } from "@/components/HubHero";
import {
  organizationSchema,
  websiteSchema,
  webPageSchema,
  breadcrumbSchema,
  faqPageSchema,
  serviceSchema,
} from "@/lib/schemas";

const ServiceLandingPage = ({ slug }) => {
  const page = SERVICE_LANDINGS[slug];
  const path = `/services/${slug}`;
  const title = page ? (page.seoTitle || `${page.h1} | Kostaja`) : "Service | Kostaja";
  const description = page?.metaDescription || page?.sub || "";
  const canonical = `${SITE.url}${path}`;

  useSeo({
    title,
    description,
    canonical,
    jsonLd: page
      ? [
          organizationSchema(),
          websiteSchema(),
          webPageSchema({ path, name: title, description }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: page.h1, path },
          ]),
          serviceSchema({
            name: page.h1,
            description: page.sub,
            path,
            category: page.eyebrow,
          }),
          faqPageSchema(page.faqs || []),
        ]
      : [],
  });

  if (!page) return <Navigate to="/services" replace />;

  const relatedServices = (page.services || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <main data-testid={`service-landing-${slug}`} className="bg-black text-white">
      {/* HERO — art-directed for the 4 macro hubs, editorial default otherwise */}
      {HUB_SLUGS.has(slug) ? (
        <HubHero page={page} />
      ) : (
        <section className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
            <Crumbs items={[{ to: "/", label: "Home" }, { to: "/services", label: "Services" }, { label: page.h1 }]} />
            <Overline className="mt-8">{page.eyebrow}</Overline>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tight leading-[0.94] max-w-5xl">
              {page.h1}
            </h1>
            <p className="mt-6 text-base lg:text-xl text-white/75 max-w-3xl leading-relaxed">{page.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryButton to="/contact" testId="landing-primary-cta">Start a Project</PrimaryButton>
              <SecondaryButton to="/services" testId="landing-secondary-cta">Explore all services</SecondaryButton>
            </div>
          </div>
        </section>
      )}

      {/* WHO FOR + PROBLEM */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <Overline className="mb-3">Who this is for</Overline>
            <p className="text-white/85 text-base lg:text-lg leading-relaxed">{page.whoFor}</p>
          </div>
          <div>
            <Overline className="mb-3">Problem this solves</Overline>
            <p className="text-white/85 text-base lg:text-lg leading-relaxed">{page.problem}</p>
          </div>
        </div>
      </section>

      {/* WHAT KOSTAJA BUILDS */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline className="mb-3">What Kostaja builds</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">The working pieces.</h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
              {page.builds.map((b, idx) => (
                <li key={b} className="bg-black p-5 lg:p-6 flex gap-3">
                  <span className="text-[10px] font-black text-[#FFC600] leading-none mt-1">0{idx + 1}</span>
                  <span className="text-white/85 text-sm lg:text-base leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline className="mb-3">Deliverables</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">What you receive.</h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="space-y-3">
              {page.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 border-b border-white/10 pb-3">
                  <span aria-hidden className="text-[#FFC600] mt-1">◆</span>
                  <span className="text-white/85">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline className="mb-3">Process</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">How the work runs.</h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="border-l border-white/25">
              {page.process.map((step, idx) => (
                <li key={step} className="pl-6 pb-6 relative">
                  <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 bg-[#FFC600] border border-white/15" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">Step 0{idx + 1}</div>
                  <div className="mt-1 text-lg font-semibold">{step}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {relatedServices.length ? (
        <section className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
              <div>
                <Overline className="mb-2">Related services</Overline>
                <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">Fixed-scope options inside this landing.</h2>
              </div>
              <Link
                to="/services"
                className="text-sm font-semibold underline underline-offset-4 hover:text-[#FFC600]"
              >
                See all services →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedServices.map((s) => <ServiceCard key={s.slug} service={s} />)}
            </div>
          </div>
        </section>
      ) : null}

      {/* FAQS */}
      {page.faqs && page.faqs.length ? (
        <section className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
            <Overline className="mb-4">Frequently asked</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95] mb-10">Common questions</h2>
            <div className="divide-y divide-white/15 border-t border-b border-white/15">
              {page.faqs.map((f) => (
                <details key={f.q} className="group py-6">
                  <summary className="flex items-center justify-between cursor-pointer text-lg lg:text-xl font-black tracking-tight">
                    {f.q}
                    <span aria-hidden className="text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-white/75 max-w-3xl leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CTABlock variant="service" />
    </main>
  );
};

export default ServiceLandingPage;
